import { useEffect, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useAccountMembership, useProjects } from '../../app/queries'
import { useBugReporterConfig, useBugReports, useDecideBugReport, usePutBugReporterConfig, type BugReport } from '../../app/bug-reports'
import { Button } from '../../components/button'
import { Modal } from '../../components/modal'
import { CreateTaskForm } from '../development-task'
import { bugTaskDraft } from './task-draft'
import { FeedbackInbox } from './inbox'

export function BugReportsScreen() {
  const projects = useProjects()
  const account = useAccountMembership()
  const navigate = useNavigate()
  const { projectId } = useParams({strict:false}) as {projectId?:string}
  const selected = projectId ?? projects.data?.items.find(item=>item.slug==='arron')?.id
  const project = projects.data?.items.find(item=>item.id===selected)
  useEffect(()=>{if(!projectId&&selected)void navigate({to:'/projects/$projectId/bug-reports',params:{projectId:selected},replace:true})},[projectId,selected,navigate])
  const [settings, setSettings] = useState(false)
  const reports = useBugReports({ project_id: selected }, Boolean(selected))
  const config = useBugReporterConfig(selected)
  const putConfig = usePutBugReporterConfig()
  const decide = useDecideBugReport()
  const [fix, setFix] = useState<BugReport>()
  const [review, setReview] = useState<{report:BugReport;decision:'close'|'request_information'}>()
  const [reason,setReason] = useState('')
  useEffect(()=>{setFix(undefined);setReview(undefined);setReason('');setSettings(false)},[selected])
  const canOperate = account.data?.capabilities?.includes('operate') ?? false
  const approveRed = account.data?.capabilities?.includes('approve_red') ?? false
  const configure = (enabled:boolean) => {
    if(!selected||!config.data)return
    putConfig.mutate({projectId:selected,allowed_origins:config.data.allowed_origins,daily_quota:config.data.daily_quota,enabled,rotate_key:false,
      ...(config.data.bug_agent_profile_id && !config.data.bug_agent_profile_id.startsWith('00000000') ? {bug_agent_profile_id:config.data.bug_agent_profile_id}: {})})
  }
  return <>
    <FeedbackInbox key={selected} name={project?.name??'项目'} reports={reports.data?.items??[]} pending={reports.isPending} error={reports.error?.message} canOperate={canOperate} approveRed={approveRed} busy={decide.isPending} actionError={decide.error?.message} onRetry={report=>decide.mutate({id:report.id,decision:'retry_analysis',expected_version:report.version})} onSettings={()=>setSettings(true)} onFix={setFix} onReview={(report,decision)=>{setReason('');setReview({report,decision})}}/>
    <Modal open={settings} onOpenChange={setSettings} title="反馈设置" description={project?.name}>
      <label className="flex items-center gap-npc-gap"><input type="checkbox" checked={config.data?.enabled??true} disabled={!config.data||putConfig.isPending} onChange={e=>configure(e.target.checked)}/>显示反馈按钮</label>
      <p className="text-npc-caption text-m-t3">只影响当前项目；关闭后，已有反馈仍保留在列表中。</p>
      {config.isError?<p role="alert">反馈配置暂时无法读取。</p>:null}
      {putConfig.error?<p role="alert">{putConfig.error.message}</p>:null}
    </Modal>
    <Modal open={Boolean(fix)} onOpenChange={open=>{if(!open)setFix(undefined)}} title="新建开发任务" description={project?.name} className="w-[min(720px,calc(100vw-32px))]">
      {fix&&project?<CreateTaskForm key={fix.id} project={project} initial={bugTaskDraft(fix,location.origin)} onCreated={async task=>{
        await decide.mutateAsync({id:fix.id,decision:'confirm_fix',expected_version:fix.version,development_task_id:task.id})
        setFix(undefined)
      }}/>:null}
    </Modal>
    {/* 「关闭反馈 / 请他补充」的理由框。这里量过两处布局毛病，都不是口味问题：
        · 理由框原来是裸 `rounded`（实算 4px）+ 无底色 + 边框吃 `currentColor`
          （浅色主题下就是正文那道深灰，比 hairline 重一档），和全站输入框差着两档。
          换成「井底 + 细描边 + 12px」——12px 是 projects-theme 锁死的输入圆角
          （projects.css `--pj-radius-input`），不是新造的一档。
        · 确认按钮原来写在 children 里。Modal 的底部按钮区是 footer 插槽
          （`justify-end`），写进 children 就变成左下角孤零零一个——
          1440 下量到它右缘离对话框内容区右缘还有 459px。挪进 footer 即回右下角。
        没动 styles.css 那条全局绿色焦点圈（「不许各屏各写一套」）。 */}
    <Modal open={Boolean(review)} onOpenChange={open=>{if(!open)setReview(undefined)}} title={review?.decision==='close'?'关闭反馈':'请他补充'} description="理由会展示给反馈人。"
      footer={<Button disabled={!reason.trim()||decide.isPending} onClick={()=>{if(review)decide.mutate({id:review.report.id,decision:review.decision,expected_version:review.report.version,reason:reason.trim()},{onSuccess:()=>setReview(undefined)})}}>确认</Button>}>
      <label className="grid gap-npc-gap-xs">
        <span className="text-npc-caption font-semibold text-npc-ink">处理理由</span>
        <textarea aria-label="处理理由" required maxLength={4000} value={reason} onChange={e=>setReason(e.target.value)} className="min-h-28 w-full resize-y rounded-npc-card-sm border border-npc-border-soft bg-m-well px-npc-gap-sm py-npc-gap-xs text-npc-body-sm text-npc-ink" />
      </label>
      {decide.error?<p role="alert" className="m-0 mt-npc-gap-sm text-npc-caption text-npc-red-ink">{decide.error.message}</p>:null}
    </Modal>
  </>
}
